
import { Certificate24, ServiceDesk24, License24, AddFilled16, Reset16 } from '@carbon/icons-react'
import { useEffect, useMemo, useState } from 'react'
import { InvoiceItemsTable } from '../components/invoice-items-table';
import { TextInput } from 'carbon-components-react';
import { FormSection, FormSectionWithoutMargin, useForm } from '../../../../templates/form/form';
import { withLoadablePage } from '../../../../base/Page';
import Api from '../../../../session/Api';
import Button from '../../../../components/Button';
import { CUSTOMER_ENTITY_TYPE, TAX_TYPES } from '..';
import useStore from '../../../../hooks/useStore';
import Util, { big, max } from '../../../../util/Util';
import UIUtil from '../../../../util/UIUtil';
import { DESTINATION_TYPE_CUSTOMER, DESTINATION_TYPE_STORE, SOURCE_TYPE_STORE, SOURCE_TYPE_WAREHOUSE } from '../../../../constants/Constants';
import { useHistory } from 'react-router-dom';
import { InvoiceableSalesOrders } from '../components/invoiceable-sales-orders';
import { AmountTagSelector, AmountTagSelectorNormalField } from '../../../../pages/journal-entries/tag-selector';
import { listDetailItemId } from '../../../../reporting-engine/base/report-page';


function CreateBtn({ store, endpoint }) {
    const [loading, setLoading] = useState(false);
    const history = useHistory();

    const amounts = useCalcAmounts(store);
    const onClick = () => {
        const {
            discount: discountValue,
            items: itemsValue,
            inventoryRequests: inventoryRequestsValue,
            tags: tagsValue,
            voucherNo,

            customerId, date, dueDate,
            againstOrderIds,

            taxId, info,
            saleType, defaultLocationId,
        } = store.toObject();

        const customer = endpoint.customers.find($ => $.id === customerId);
        const items = itemsValue.filter($ => $.item?.selectedItem?.type)
        const inventoryRequests = inventoryRequestsValue.filter($ => $.item?.selectedItem?.type) ?? []
        const tags = tagsValue.map(val => ({ ...val, name: val.label }))
        const tax = TAX_TYPES.find(tax => tax.id === taxId);

        if (!tax) {
            UIUtil.showInfo("No tax code selected");
            return;
        }

        if (!date || date <= 0) {
            UIUtil.showInfo("No date set")
            return;
        }

        if (!dueDate || dueDate <= 0) {
            UIUtil.showInfo("No due date set")
            return;
        }

        if (!customer) {
            UIUtil.showInfo("No customer selected")
            return;
        }

        if (items.length === 0) {
            UIUtil.showInfo("No products/services in invoice")
            return;
        }

        if (Util.isStringExists(discountValue) && amounts.discount <= 0) {
            UIUtil.showInfo("Invalid discount value")
            return;
        }

        if (amounts.maxDiscountReached) {
            //UIUtil.showInfo("Discount cannot be greater than: " + amounts.maxDiscount);
            UIUtil.showInfo(`Discount must either be less than ${amounts.maxDiscount} or be ${amounts.freeWithTaxAmount}`)
            return;
        }

        const customItem = items.find($ => !$.item.selectedItem);
        if (customItem) {
            if (Util.isStringExists(customItem.item.name)) {
                UIUtil.showInfo(`Item '${customItem.item.name}' does not exist`)
            } else {
                UIUtil.showInfo("Please ensure every row has an item selected")
            }
            return;
        }


        // console.log({ inventoryRequests })
        setLoading(true)
        const req = {
            voucherNo,
            invoiceDate: date,
            dueDate: dueDate,
            changeGmtTime: true,
            creditNote: saleType === "creditNote",

            defaultLocationId,

            invoiceToName: customer.name,
            invoiceToTrn: customer.trn,
            invoiceToAddress: customer.address,

            items: items.concat(inventoryRequests).map(item => ({
                customerId: customerId,

                itemId: item.item.selectedItem?.id,
                itemType: item.item.selectedItem?.type,

                rate: big(item.amount).toFixed(4),
                qty: big(item.qty).toFixed(4),
                tax: amounts.itemTaxPerc(item).toFixed(4),
                dividedDiscount: amounts.itemDiscount(item).toFixed(4),

                taxName: Util.isStringExists(item?.taxName) ? item?.taxName : tax.value,

                tags: item.tags?.map(val => ({ ...val, name: val.label })) ?? [],

                hidden: item.hidden,
                memo: item.memo,
                fromServiceId: item.fromServiceId
            })),

            // inventoryRequests: inventoryRequests?.map(item => ({
            //     itemId: item.item.selectedItem?.id,
            //     qty: big(item.qty).toFixed(2),
            // })),

            memo: info,
            tags: tags,
            againstOrderIds: againstOrderIds,
        }

        // console.log(req)
        Api.createV2Sale(req, response => {
            if (response.status === true) {
                UIUtil.showSuccess();
                //history.replace("/stock-flow/" + response.payload)
                history.replace(listDetailItemId("V2SalesList", response.payload.id))
            } else {
                UIUtil.showError(response.message)
            }
            setLoading(false);
        })
    }

    return <Button loading={loading} onClick={onClick} size={"sm"} renderIcon={AddFilled16} style={{ borderRadius: 7 }}>Create</Button>
}

function useCalcAmounts(store) {
    const [items] = useStore(store, "items");
    const [taxId] = useStore(store, "taxId");
    const [discountValue] = useStore(store, "discount");

    return useMemo(() => {
        const defTaxPerc = TAX_TYPES.find(tax => tax.id === taxId)?.perc ?? 0;
        const totalDiscount = max(big(discountValue), big(0));

        const getTotals = (discount, itemTaxPercFn) => {
            const gross = items.filter($ => $.item?.selectedItem?.type).map(item => big(item.qty).times(big(item.amount))).reduce((t, c) => t.add(c), big(0));
            const discountPerc = gross.gt(0) ? discount.div(gross) : big(0)
            const net = gross.minus(discountPerc.times(gross))

            const tax = items
                .filter($ => $.item?.selectedItem?.type)
                .map(item => {
                    const itemGross = big(item.qty).times(big(item.amount));
                    //const itemTaxPerc = big(TAX_TYPES.find(tax => tax.value === item?.taxName)?.perc ?? defTaxPerc).div(100);
                    const itemTaxPerc = itemTaxPercFn(item);
                    const itemDiscountPerc = discountPerc;

                    return itemGross.minus(itemDiscountPerc.times(itemGross)).times(itemTaxPerc);
                    // return itemGross.times(itemTaxPerc)
                })
                .reduce((t, c) => t.add(c), big(0));

            const total = net.add(tax);

            return { gross, discountPerc, net, tax, total }
        }



        const { gross: maxDiscount, total: freeWithTaxAmount } = getTotals(big(0), item => big(TAX_TYPES.find(tax => tax.value === item?.taxName)?.perc ?? defTaxPerc).div(100));
        const itemTaxPerc = item => {
            if (totalDiscount.round(4).eq(freeWithTaxAmount.round(4))) {
                return big(-1);
            } else {
                return big(TAX_TYPES.find(tax => tax.value === item?.taxName)?.perc ?? defTaxPerc).div(100);;
            }
        };

        //28.57143 1476 44280
        // console.log({
        //     "eq": totalDiscount.round(4).eq(freeWithTaxAmount.round(4)),
        //     "eq2": big(totalDiscount.toFixed(2)).eq(big(freeWithTaxAmount.toFixed(2))),
        //     "eq3": big(totalDiscount.toFixed(4)).eq(big(freeWithTaxAmount.toFixed(4))),
        //     "@2total dis": totalDiscount.round(4),
        //     "@2free amt": freeWithTaxAmount.round(4),
        //     "total dis": totalDiscount.round(4).toFixed(4),
        //     "free amt": freeWithTaxAmount.round(4).toFixed(4),
        //     "actotal": freeWithTaxAmount.round(4).toNumber(),
        //     "ac1122": getTotals(totalDiscount, itemTaxPerc).total.round(4).toNumber()
        // })

        const { gross, discountPerc, net, tax, total } = getTotals(totalDiscount, itemTaxPerc);

        const num = n => n.round(4).toNumber();
        return {
            gross: num(gross),
            discount: num(totalDiscount),
            net: num(net),
            tax: num(tax),
            total: num(total),

            itemTaxPerc,
            itemDiscount: item => {
                const itemGross = big(item.qty).times(big(item.amount));
                const itemDiscountPerc = discountPerc;
                return itemDiscountPerc.times(itemGross)
            },

            maxDiscount: num(maxDiscount),
            freeWithTaxAmount: num(freeWithTaxAmount),
            maxDiscountReached: totalDiscount.gt(maxDiscount) && !totalDiscount.round(4).eq(freeWithTaxAmount.round(4))
        }
    }, [items, taxId, discountValue])
}

const Items = ({ sellables, predefinedClasses, editable, value, setValue }) => {
    // console.log(value)
    return <InvoiceItemsTable sellables={sellables} predefinedClasses={predefinedClasses} editable={editable} items={value} setItems={setValue} />
}

const InventorySource = ({ store, endpoint }) => {
    const [customerId] = useStore(store, "customerId");
    const customer = useMemo(() => endpoint.customers.find($ => $.id === customerId), [customerId, endpoint])

    const fromCustomerLocation = customer?.type === CUSTOMER_ENTITY_TYPE.VENUE;

    return (
        <div style={{ width: '100%', height: '2rem', background: '#cecece', borderTop: '1px solid #00000020', paddingInline: '3rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <p style={{ opacity: 0.65, fontSize: 12 }}>Inventory source: </p>
            <p style={{ fontSize: 12, color: fromCustomerLocation ? '#0f62fe' : undefined, fontWeight: fromCustomerLocation ? 'bold' : undefined }}>
                {fromCustomerLocation ? customer.value : 'Main Warehouse'}
            </p>
        </div>
    )
}

const BottomBar = ({ store, endpoint }) => {
    const amounts = useCalcAmounts(store);
    console.log({ amounts })
    return (
        <div style={{ paddingInline: '3rem' }}>
            <div style={{ height: '4rem', width: '100%', boxShadow: '0px 10px 15px -3px rgba(0,0,0,0.1) , 0px 4px 6px -2px rgba(0,0,0,0.05) ', background: '#f4f4f4', border: '1px solid #00000020', display: 'flex', alignItems: 'center', gap: '1.5rem', paddingInline: '1rem', paddingBlock: '1rem', borderRadius: 10, marginTop: '4rem', color: 'black' }}>
                <div>
                    <label className="bx--label" style={{ opacity: 0.65, marginBottom: 0 }}>Gross</label>
                    <h4 style={{}}>AED {Util.formatMoney(amounts.gross)}</h4>
                </div>
                <h4>-</h4>
                <div>
                    <label className="bx--label" style={{ opacity: 0.65, marginBottom: 0 }}>Discount</label>
                    <h4 style={{}}>AED {Util.formatMoney(amounts.discount)}</h4>
                </div>
                <h4>=</h4>
                <div>
                    <label className="bx--label" style={{ opacity: 0.65, marginBottom: 0 }}>Net</label>
                    <h4 style={{}}>AED {Util.formatMoney(amounts.net)}</h4>
                </div>
                <h4>+</h4>
                <div>
                    <label className="bx--label" style={{ opacity: 0.65, marginBottom: 0 }}>Tax</label>
                    <h4 style={{}}>AED {Util.formatMoney(amounts.tax)}</h4>
                </div>
                <h4>=</h4>
                <div>
                    <h3 style={{ fontWeight: 'bold', color: '#009900' }}>AED {Util.formatMoney(amounts.total, 4)}</h3>
                </div>
                <div style={{ flex: 1 }} />

                <CreateBtn store={store} endpoint={endpoint} />
            </div>
        </div>
    )
    // return (
    //     <div style={{ height: '4rem', width: '100%', background: '#1c1c1c', display: 'flex', alignItems: 'center', gap: '1.5rem', paddingInline: '3rem', color: 'white' }}>
    //         <div>
    //             <label className="bx--label" style={{ color: 'white', opacity: 0.65, marginBottom: 0 }}>Gross</label>
    //             <h4 style={{}}>AED {Util.formatMoney(amounts.gross)}</h4>
    //         </div>
    //         <h4>-</h4>
    //         <div>
    //             <label className="bx--label" style={{ color: 'white', opacity: 0.65, marginBottom: 0 }}>Discount</label>
    //             <h4 style={{}}>AED {Util.formatMoney(amounts.discount)}</h4>
    //         </div>
    //         <h4>=</h4>
    //         <div>
    //             <label className="bx--label" style={{ color: 'white', opacity: 0.65, marginBottom: 0 }}>Net</label>
    //             <h4 style={{}}>AED {Util.formatMoney(amounts.net)}</h4>
    //         </div>
    //         <h4>+</h4>
    //         <div>
    //             <label className="bx--label" style={{ color: 'white', opacity: 0.65, marginBottom: 0 }}>Tax</label>
    //             <h4 style={{}}>AED {Util.formatMoney(amounts.tax)}</h4>
    //         </div>
    //         <h4>=</h4>
    //         <div>
    //             <h3 style={{ fontWeight: 'bold', color: '#009900' }}>AED {Util.formatMoney(amounts.total)}</h3>
    //         </div>
    //         <div style={{ flex: 1 }} />

    //         <CreateBtn store={store} endpoint={endpoint} />
    //     </div>
    // )
}

function AgainstOrdersCount({ store }) {
    const [startDate] = useStore(store, "salesOrderStartDate");
    const [endDate] = useStore(store, "salesOrderEndDate");
    const [value] = useStore(store, "againstOrderIds");

    if (Util.isNumberExist(startDate) && Util.isNumberExist(endDate)) {
        return (
            <div style={{ width: '100%', height: 25, borderRadius: 7, background: '#006000', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <p style={{ fontSize: 12, color: 'white', textAlign: 'center' }}>against {(value?.length ?? 0) + ''} orders</p>
            </div>
        )
    } else {
        return (
            <div style={{ width: '100%', height: 25, borderRadius: 7, background: '#00000020', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <p style={{ fontSize: 12, color: 'black', textAlign: 'center' }}>
                    select from and to dates
                </p>
            </div>
        )
    }
}

function SelectSalesOrders({ store, sellables, predefinedClasses }) {
    const [customerId] = useStore(store, "customerId");
    const [selectionMode] = useStore(store, "salesOrderSelection");
    const [startDate] = useStore(store, "salesOrderStartDate");
    const [endDate] = useStore(store, "salesOrderEndDate");

    const [items, setItems] = useStore(store, "items");
    const [inventoryRequests, setInventoryRequests] = useStore(store, "inventoryRequests");
    const [againstOrderIds, setAgainstOrderIds] = useStore(store, "againstOrderIds")

    if (Util.isNumberExist(startDate) && Util.isNumberExist(endDate)) {
        return (
            <InvoiceableSalesOrders {...{
                customerId, selectionMode, startDate, endDate,
                items, setItems, againstOrderIds, setAgainstOrderIds,
                predefinedClasses, sellables,
                inventoryRequests, setInventoryRequests
            }} />
        )
    } else {
        return (
            <div style={{ paddingInline: '3rem' }}>
                <p style={{}}>Please select from and to dates to select orders in range to continue</p>
            </div>
        )
    }
}

function TagSelector({ predefinedClasses, value, setValue }) {
    return <AmountTagSelectorNormalField options={predefinedClasses} value={value} setValue={setValue} />
}

const salesOrderSelectionOptions = [
    { id: 0, title: "No orders" },
    { id: 2, title: "Select orders" }
    // { id: 1, title: "All orders" },
    // { id: 2, title: "Orders in range" }
]

function View({ payload: endpoint }) {
    const form = useForm(store => {
        store.set("salesOrderSelection", 0);
        store.set("salesOrderStartDate", 0)
        store.set("salesOrderEndDate", 0)

        store.set("date", new Date().getTime());
        store.set("dueDate", new Date().getTime());
        store.set("items", [{ tempId: Util.newTempId() }])
        store.set("inventoryRequests", [])
        store.set("againstOrderIds", []);

        store.set("taxId", TAX_TYPES[0].id)
        store.set("discount", 0)

        store.set("tags", []);
    }, {
        skipFieldEffect: true
    })

    form.useChangeListener('saleType', () => {
        form.setField("salesOrderSelection", 0)
        form.setField("salesOrderStartDate", 0)
        form.setField("salesOrderEndDate", 0)
    })

    form.useChangeListener('customerId', () => {
        form.setField("salesOrderSelection", 0)
        form.setField("salesOrderStartDate", 0)
        form.setField("salesOrderEndDate", 0)
    })

    form.useChangeListener('salesOrderSelection', mode => {
        form.setField('items', [{ tempId: Util.newTempId() }])
        form.setField('inventoryRequests', [])
        form.setField('againstOrderIds', [])
        form.setField("salesOrderStartDate", 0)
        form.setField("salesOrderEndDate", 0)
    })

    return (<>
        <div style={{ height: 'calc(100vh - 3rem)', overflow: 'auto', paddingTop: '6rem', paddingBottom: '16rem' }}>
            <div style={{ paddingInline: '3rem', }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                    <Certificate24 />
                    <h4 style={{ fontWeight: 'bold' }}>Create Sale</h4>
                </div>

                <div style={{ display: 'flex', alignItems: 'stretch', gap: '1rem', marginBottom: '3rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', columnGap: '1rem', alignContent: 'space-between', flex: 2 }}>
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            <div style={{ flex: 1 }}>
                                <form.SwitchField _skipFormMargin fieldKey="saleType" title="Type" options={[{ id: "sale", title: "Invoice" }, { id: "creditNote", title: "Credit Note" }]} />
                            </div>
                            <div style={{ flex: 1 }}>
                                <form.ComboBoxField _skipFormMargin fieldKey="customerId" title="Customer" options={endpoint.customers} />
                            </div>
                        </div>
                        <form.TextField _skipFormMargin fieldKey="voucherNo" title="Voucher No (optional)" placeholder="(auto-generated if empty)" />
                        {/* <div /> */}

                        <form.DateField _skipFormMargin fieldKey="date" title="Invoice Date" />
                        <form.DateField _skipFormMargin fieldKey="dueDate" title="Due Date" />
                    </div>
                    <div style={{ flex: 1 }}>
                        <form.TextAreaField _skipFormMargin style={{ height: 124 }} fieldKey="info" title="Memo (optional)" />
                    </div>
                </div>

                <form.IfTargetGroup targetKey={"saleType"} checkTargetIf={type => type !== "creditNote"}>
                    <form.IfTargetGroup targetKey={"customerId"} checkTargetIf={customerId => endpoint.customers.find($ => $.id === customerId)?.hasOrders === true}>
                        <FormSection title="Sales Order">
                            <form.SwitchField fieldKey="salesOrderSelection" title="Against orders?" options={salesOrderSelectionOptions} />

                            <form.IfTargetGroup targetKey={"salesOrderSelection"} checkTargetIf={selection => selection === 2}>
                                <div style={{ display: 'flex', gap: '1rem' }}>
                                    <div style={{ flex: 1 }}>
                                        <form.DateField fieldKey="salesOrderStartDate" title="Start date" />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <form.DateField fieldKey="salesOrderEndDate" title="End date" />
                                    </div>
                                </div>
                            </form.IfTargetGroup>

                            <form.IfTargetGroup targetKey={"salesOrderSelection"} checkTargetIf={selection => selection === 1 || selection === 2}>
                                <form.ExtField>
                                    {AgainstOrdersCount}
                                </form.ExtField>
                                {/* <form.CustomField fieldKey="againstOrderIds">
                                    {AgainstOrdersCount}
                                </form.CustomField> */}
                            </form.IfTargetGroup>
                        </FormSection>
                    </form.IfTargetGroup>
                </form.IfTargetGroup>

                <FormSectionWithoutMargin title="Items">
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', columnGap: '1rem', marginBottom: '0rem' }}>
                        <form.ComboBoxField fieldKey="taxId" title="Default Tax Code" options={TAX_TYPES} />
                        <form.TextField fieldKey="discount" title="Discount" />
                        <div>
                            <form.CustomField fieldKey="tags" title="Class" childrenProps={{ predefinedClasses: endpoint.predefinedClasses }}>
                                {TagSelector}
                            </form.CustomField>
                        </div>
                        <form.ComboBoxField fieldKey="defaultLocationId" title="Default Location" options={endpoint.locations} />
                    </div>
                </FormSectionWithoutMargin>
            </div>
            <div style={{ height: '3rem' }} />
            <form.IfTargetGroup targetKey={"salesOrderSelection"} checkTargetIf={selection => selection === 1 || selection === 2}>
                <form.ExtField childrenProps={{ sellables: endpoint.sellables, predefinedClasses: endpoint.predefinedClasses }}>
                    {SelectSalesOrders}
                </form.ExtField>
            </form.IfTargetGroup>
            <form.IfTargetGroup targetKey={"salesOrderSelection"} checkTargetIf={selection => selection === 0}>
                <form.CustomField fieldKey="items" childrenProps={{ sellables: endpoint.sellables, predefinedClasses: endpoint.predefinedClasses, editable: true, }}>
                    {Items}
                </form.CustomField>
            </form.IfTargetGroup>

            <form.ExtField _skipFormMargin childrenProps={{ endpoint }}>
                {BottomBar}
            </form.ExtField>
        </div>
    </>)

    // return (<>
    //     <div style={{ height: 'calc(100vh - 1rem - 6rem)', overflow: 'auto' }}>
    //         <div style={{ display: 'flex', alignItems: 'stretch', minHeight: '100%' }}>
    //             <div style={{ flex: 1 }}>
    //                 <div style={{ paddingInline: '3rem', paddingBlock: '3rem', }}>

    //                 </div>
    //             </div>
    //             <div style={{ flex: 3, background: '#f4f4f4', borderLeft: '1px solid #00000005' }}>
    //                 <div style={{ paddingInline: '3rem', paddingBlock: '3rem' }}>
    //                     <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
    //                         <ServiceDesk24 />
    //                         <h4 style={{ fontWeight: 'bold', flex: 1 }}>Products/Services</h4>
    //                         <p style={{ fontSize: 12, opacity: 0.65 }}>Tax NOT included</p>
    //                     </div>
    //                 </div>

    //                 <form.IfTargetGroup targetKey={"salesOrderSelection"} checkTargetIf={selection => selection === 1 || selection === 2}>
    //                     <form.ExtField childrenProps={{ sellables: endpoint.sellables, predefinedClasses: endpoint.predefinedClasses }}>
    //                         {SelectSalesOrders}
    //                     </form.ExtField>
    //                 </form.IfTargetGroup>
    //                 <form.IfTargetGroup targetKey={"salesOrderSelection"} checkTargetIf={selection => selection === 0}>
    //                     <form.CustomField fieldKey="items" childrenProps={{ sellables: endpoint.sellables, predefinedClasses: endpoint.predefinedClasses, editable: true, }}>
    //                         {Items}
    //                     </form.CustomField>
    //                 </form.IfTargetGroup>
    //             </div>
    //         </div>
    //     </div>

    // </>)

    // return (<>
    //     <div style={{ height: 'calc(100vh - 1rem - 6rem)', overflow: 'auto' }}>
    //         <div style={{ display: 'flex', alignItems: 'stretch', minHeight: '100%' }}>
    //             <div style={{ flex: 1 }}>
    //                 <div style={{ paddingInline: '3rem', paddingBlock: '3rem', }}>
    //                     <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
    //                         <Certificate24 />
    //                         <h4 style={{ fontWeight: 'bold' }}>Create Sale</h4>
    //                     </div>

    //                     <FormSection>
    //                         <div style={{ display: 'flex', gap: '1rem' }}>
    //                             <div style={{ flex: 1 }}>
    //                                 <form.DateField fieldKey="date" title="Date" />
    //                             </div>
    //                             <div style={{ flex: 1 }}>
    //                                 <form.DateField fieldKey="dueDate" title="Due Date" />
    //                             </div>
    //                         </div>
    //                         <form.ComboBoxField fieldKey="customerId" title="Customer" options={endpoint.customers} />

    //                         <form.IfTargetGroup targetKey={"customerId"} checkTargetIf={customerId => endpoint.customers.find($ => $.id === customerId)?.hasOrders === true}>
    //                             <form.SwitchField fieldKey="salesOrderSelection" title="Against orders?" options={salesOrderSelectionOptions} />

    //                             <form.IfTargetGroup targetKey={"salesOrderSelection"} checkTargetIf={selection => selection === 2}>
    //                                 <div style={{ display: 'flex', gap: '1rem' }}>
    //                                     <div style={{ flex: 1 }}>
    //                                         <form.DateField fieldKey="salesOrderStartDate" title="Start date" help="At 12am" />
    //                                     </div>
    //                                     <div style={{ flex: 1 }}>
    //                                         <form.DateField fieldKey="salesOrderEndDate" title="End date" help="At 5am of next day" />
    //                                     </div>
    //                                 </div>
    //                             </form.IfTargetGroup>

    //                             <form.IfTargetGroup targetKey={"salesOrderSelection"} checkTargetIf={selection => selection === 1 || selection === 2}>
    //                                 <form.CustomField fieldKey="againstOrderIds">
    //                                     {AgainstOrdersCount}
    //                                 </form.CustomField>
    //                             </form.IfTargetGroup>
    //                         </form.IfTargetGroup>
    //                     </FormSection>

    //                     <FormSection title={"Amounts"}>
    //                         <form.ComboBoxField fieldKey="taxId" title="Default Tax Code" options={TAX_TYPES} />
    //                         <form.TextField fieldKey="discount" title="Discount" />
    //                     </FormSection>


    //                     <FormSection title={"Additional Info"}>
    //                         <form.CustomField fieldKey="tags" title="Class" childrenProps={{ predefinedClasses: endpoint.predefinedClasses }}>
    //                             {TagSelector}
    //                         </form.CustomField>
    //                         <form.TextAreaField fieldKey="info" title="Memo (optional)" />
    //                     </FormSection>

    //                 </div>
    //             </div>
    //             <div style={{ flex: 3, background: '#f4f4f4', borderLeft: '1px solid #00000005' }}>
    //                 <div style={{ paddingInline: '3rem', paddingBlock: '3rem' }}>
    //                     <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
    //                         <ServiceDesk24 />
    //                         <h4 style={{ fontWeight: 'bold', flex: 1 }}>Products/Services</h4>
    //                         <p style={{ fontSize: 12, opacity: 0.65 }}>Tax NOT included</p>
    //                     </div>
    //                 </div>

    //                 <form.IfTargetGroup targetKey={"salesOrderSelection"} checkTargetIf={selection => selection === 1 || selection === 2}>
    //                     <form.ExtField childrenProps={{ sellables: endpoint.sellables, predefinedClasses: endpoint.predefinedClasses }}>
    //                         {SelectSalesOrders}
    //                     </form.ExtField>
    //                 </form.IfTargetGroup>
    //                 <form.IfTargetGroup targetKey={"salesOrderSelection"} checkTargetIf={selection => selection === 0}>
    //                     <form.CustomField fieldKey="items" childrenProps={{ sellables: endpoint.sellables, predefinedClasses: endpoint.predefinedClasses, editable: true, }}>
    //                         {Items}
    //                     </form.CustomField>
    //                 </form.IfTargetGroup>
    //             </div>
    //         </div>
    //     </div>
    //     <form.ExtField _skipFormMargin childrenProps={{ endpoint }}>
    //         {BottomBar}
    //     </form.ExtField>
    // </>)
}

export const NewSales = withLoadablePage(listener => Api.getV2SalesEndpoint(listener), View);